import { Button, Grid, IconButton, Stack, TextField } from '@mui/material';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import type { NewCustomer, UpdateCustomer } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AddIcon } from '../../../../../../components/icons/AddIcon';
import { VyIcon } from '@vy/ui-design-system';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import type { CreateCustomerMode } from '../../../create-customer-page-types/CreateCustomerModeType';
import { colors } from '../../../../../../assets/themes/colors.ts';
import { VyFormSection } from '../../../../../../components/design/layout';

export const CreateCustomerGeneralInformation: React.FC<CreateCustomerMode> = ({
  mode: _mode = 'create',
}) => {
  const { getValues, setValue, register, formState } =
    useFormContext<typeof _mode extends 'create' ? NewCustomer : UpdateCustomer>();

  register('taxIds');

  const existingTaxIds = getValues('taxIds') ?? [];
  const [additionalTaxIdCount, setAdditionalTaxIdCount] = useState(Math.max(existingTaxIds.length - 1, 0));

  const updateTaxId = (index: number, value: string) => {
    const current = [...(getValues('taxIds') ?? [])];
    current[index] = value;
    const filtered = current.filter(Boolean);
    setValue('taxIds', filtered.length > 0 ? filtered : [], { shouldDirty: true, shouldTouch: true });
  };

  const removeTaxId = (index: number) => {
    const current = [...(getValues('taxIds') ?? [])];
    current.splice(index, 1);
    const filtered = current.filter(Boolean);
    setValue('taxIds', filtered.length > 0 ? filtered : [], { shouldDirty: true, shouldTouch: true });
    setAdditionalTaxIdCount((prev) => prev - 1);
  };

  return (
    <VyFormSection
      title="General information"
      icon={<VyIcon icon="Info" height={18} width={18} viewBox="0 0 24 24" color={colors.grey[90]} />}
    >
      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.name)}
            label="Name"
            placeholder="e.g. Acme"
            fullWidth
            {...vyFormRegister(register, 'name')}
            defaultValue={getValues('name')}
            error={isNotNullOrUndefined(formState.errors.name)}
            helperText={formState.errors.name?.message}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.legalName)}
            label="Legal name"
            placeholder="e.g. Acme Technologies Ltd."
            {...vyFormRegister(register, 'legalName')}
            defaultValue={getValues('legalName')}
            fullWidth
            error={isNotNullOrUndefined(formState.errors.legalName)}
            helperText={formState.errors.legalName?.message}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.taxIds)}
            label="Tax ID"
            placeholder="e.g. 12-3456789"
            fullWidth
            defaultValue={existingTaxIds[0] ?? ''}
            onChange={(e) => updateTaxId(0, e.target.value.trim())}
            error={isNotNullOrUndefined(formState.errors.taxIds)}
            helperText={formState.errors.taxIds?.message}
          />
        </Grid>

        {Array.from({ length: additionalTaxIdCount }).map((_, i) => {
          const taxIdIndex = i + 1;
          return (
            <Grid key={taxIdIndex} size={4}>
              <Stack direction="row" spacing={1} sx={{
                alignItems: "flex-end"
              }}>
                <TextField
                  label="Tax ID"
                  placeholder="e.g. 12-3456789"
                  fullWidth
                  defaultValue={existingTaxIds[taxIdIndex] ?? ''}
                  onChange={(e) => updateTaxId(taxIdIndex, e.target.value.trim())}
                />
                <IconButton onClick={() => removeTaxId(taxIdIndex)} sx={{ padding: 1.5 }}>
                  <VyIcon icon="Truncate" />
                </IconButton>
              </Stack>
            </Grid>
          );
        })}

        <Grid size={12}>
          <Button
            startIcon={<AddIcon fill="#000" />}
            onClick={() => setAdditionalTaxIdCount((prev) => prev + 1)}
            sx={{ color: '#000', textTransform: 'none' }}
          >
            Add another tax registration
          </Button>
        </Grid>
      </Grid>
    </VyFormSection>
  );
};
